import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import applicationService from '../../services/applicationService';
import './MyApplication.css';

const MyApplications = () => {
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchApplications();
    fetchStats();
  }, [filter]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await applicationService.getMyApplications(params);
      setApplications(response.data || []);
      setPagination(response.pagination || {});
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to fetch applications');
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await applicationService.getMyStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleWithdraw = async (id) => {
    if (!window.confirm('Are you sure you want to withdraw this application?')) {
      return;
    }

    try {
      await applicationService.withdrawApplication(id);
      toast.success('Application withdrawn successfully');
      fetchApplications();
      fetchStats();
    } catch (error) {
      console.error('Error withdrawing application:', error);
      toast.error(error.response?.data?.message || 'Failed to withdraw application');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      reviewed: 'badge-info',
      shortlisted: 'badge-primary',
      interview: 'badge-success',
      interview_scheduled: 'badge-success',
      interviewed: 'badge-success',
      offered: 'badge-success',
      hired: 'badge-success',
      rejected: 'badge-danger'
    };
    return badges[status] || 'badge-secondary';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'hourglass_empty',
      reviewed: 'visibility',
      shortlisted: 'star',
      interview: 'video_camera_front',
      interview_scheduled: 'video_camera_front',
      interviewed: 'video_camera_front',
      offered: 'workspace_premium',
      hired: 'check_circle',
      accepted: 'check_circle',
      rejected: 'cancel'
    };
    return icons[status] || 'description';
  };

  const getStatusMessage = (status) => {
    const messages = {
      pending: 'Your application is under review',
      reviewed: 'Employer has reviewed your application',
      shortlisted: 'Congratulations! You have been shortlisted',
      interview: 'Interview scheduled - Check details below',
      interview_scheduled: 'Interview scheduled - Check details below',
      interviewed: 'Interview completed - Awaiting feedback',
      offered: 'Congratulations! You received a job offer',
      hired: 'Congratulations! You are hired',
      accepted: 'Offer accepted - Welcome aboard!',
      rejected: 'Application was not successful this time'
    };
    return messages[status] || 'Application submitted';
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    return 'Just now';
  };

  const statusConfig = {
    pending:            { label: 'Pending',    color: 'orange', icon: 'hourglass_empty' },
    reviewed:           { label: 'Reviewed',   color: 'blue',   icon: 'visibility' },
    shortlisted:        { label: 'Shortlisted',color: 'purple', icon: 'star' },
    interview:          { label: 'Interview',  color: 'teal',   icon: 'video_camera_front' },
    interview_scheduled:{ label: 'Interview',  color: 'teal',   icon: 'video_camera_front' },
    interviewed:        { label: 'Interviewed',color: 'teal',   icon: 'video_camera_front' },
    offered:            { label: 'Offered',    color: 'green',  icon: 'workspace_premium' },
    hired:              { label: 'Hired',      color: 'green',  icon: 'check_circle' },
    accepted:           { label: 'Accepted',   color: 'green',  icon: 'check_circle' },
    rejected:           { label: 'Rejected',   color: 'red',    icon: 'cancel' },
  };

  const getCfg = (s) => statusConfig[s] || { label: s, color: 'gray', icon: 'description' };

  // Horizontal Status Pipeline Component
  const HorizontalStatusPipeline = ({ statusHistory, currentStatus }) => {
    const statusOrder = ['pending', 'reviewed', 'shortlisted', 'interviewed', 'offered', 'accepted', 'rejected'];
    const pipelineSteps = statusOrder.filter(status =>
      statusHistory.some(h => h.status === status) || status === currentStatus
    );

    return (
      <div className="horizontal-pipeline">
        {pipelineSteps.map((status, index) => {
          const historyItem = statusHistory.find(h => h.status === status);
          const isCompleted = historyItem || status === currentStatus;
          const isCurrent = status === currentStatus;
          const cfg = getCfg(status);

          return (
            <React.Fragment key={status}>
              <div className={`horizontal-step ${isCompleted ? 'completed' : 'pending'} ${isCurrent ? 'current' : ''}`}>
                {isCurrent ? (
                  <span className="material-symbols-outlined horizontal-icon current-star">star</span>
                ) : isCompleted ? (
                  <span className="material-symbols-outlined horizontal-icon">check_circle</span>
                ) : (
                  <span className="material-symbols-outlined horizontal-icon">radio_button_unchecked</span>
                )}
                <span className="horizontal-label">{cfg.label}</span>
              </div>
              {index < pipelineSteps.length - 1 && (
                <div className={`horizontal-connector ${isCompleted ? 'completed' : 'pending'}`}></div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const tabList = [
    { key: 'all',        label: 'All' },
    { key: 'pending',    label: 'Pending',    dot: 'orange' },
    { key: 'shortlisted',label: 'Shortlisted',dot: 'purple' },
    { key: 'interviewed',label: 'Interview',  dot: 'teal' },
    { key: 'offered',    label: 'Offered',    dot: 'green' },
    { key: 'rejected',   label: 'Rejected',   dot: 'red' },
  ];

  if (loading) {
    return (
      <div className="candidate-my-application">
        <div className="ma-loading"><div className="ma-spinner"></div><p>Loading…</p></div>
      </div>
    );
  }

  return (
    <div className="candidate-my-application">
      {/* Header */}
      <div className="ma-header">
        <div>
          <h1 className="ma-title">My Applications</h1>
          <p className="ma-subtitle">Track the status of all your job applications</p>
        </div>
        <Link to="/jobs" className="ma-cta-btn">
          <span className="material-symbols-outlined">search</span>Browse Jobs
        </Link>
      </div>

      {/* Stats */}
      {stats && (
        <div className="ma-stats">
          <div className="ma-stat blue">
            <div className="ma-stat-top"><div className="ma-stat-icon"><span className="material-symbols-outlined">description</span></div></div>
            <div className='ma-stat-right'>
            <div className="ma-stat-num">{stats.totalApplications || 0}</div>
            <div className="ma-stat-label">Total</div>
            </div>
          </div>
          {(['pending','shortlisted','interview','offered','rejected']).map(s => {
            const cnt = stats.byStatus?.find(b => b._id === s)?.count || 0;
            const cfg = getCfg(s);
            return (
              <div key={s} className={`ma-stat ${cfg.color}`}>
                <div className="ma-stat-top">
                  <div className="ma-stat-icon">
                  <span className="material-symbols-outlined">{cfg.icon}</span>
                  </div>
                  </div>
                  <div className='ma-stat-right'>
                <div className="ma-stat-num">{cnt}</div>
                <div className="ma-stat-label">{cfg.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="ma-tabs">
        {tabList.map(t => (
          <button key={t.key} className={`ma-tab ${filter === t.key ? 'active' : ''}`} onClick={() => setFilter(t.key)}>
            {t.dot && <span className={`ma-tab-dot ${t.dot}`}></span>}
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      {applications.length === 0 ? (
        <div className="ma-empty">
          <span className="material-symbols-outlined">inbox</span>
          <h3>No applications found</h3>
          <p>Start applying for jobs to see them here</p>
          <Link to="/jobs" className="ma-cta-btn"><span className="material-symbols-outlined">search</span>Browse Jobs</Link>
        </div>
      ) : (
        <div className="ma-list">
          {applications.map((application) => {
            const cfg = getCfg(application.status);
            const job = application.job;
            return (
              <div key={application._id} className="ma-item">
                <div className="ma-item-left">
                  {job?.company?.logo
                    ? <img src={job.company.logo} alt={job.company.name} className="ma-company-logo" />
                    : <div className="ma-company-placeholder">{job?.title?.charAt(0) || '?'}</div>
                  }
                  <div className="ma-item-body">
                    <div className="ma-item-title-row">
                      <h3>{job?.title || 'Unknown Job'}</h3>
                      <span className={`ma-status-badge ${cfg.color}`}>
                        <span className="material-symbols-outlined">{cfg.icon}</span>
                        {cfg.label}
                      </span>
                       {application.statusHistory && application.statusHistory.length > 0 && (
                        <HorizontalStatusPipeline
                          statusHistory={application.statusHistory}
                          currentStatus={application.status}
                        />
                      )}
                    </div>

                    {
                      job?.company?.name &&
                      <p className="ma-company-name">{job?.company?.name}</p>
                    }
                    <div className="ma-item-meta">
                      <span><span className="material-symbols-outlined">location_on</span>{job?.location?.city?.name || 'Remote'}</span>
                      <span><span className="material-symbols-outlined">schedule</span>Applied {getTimeAgo(application.createdAt)}</span>
                      {application.expectedSalary && (
                        <span><span className="material-symbols-outlined">currency_rupee</span>${application.expectedSalary.toLocaleString()}</span>
                      )}
                    </div>
                    <p className="ma-status-msg">{getStatusMessage(application.status)}</p>
                  </div>
                </div>

                <div className='interview_main'>
                {/* Interview banner */}
                {application.interview?.scheduledDate && (
                  <div className="ma-interview-banner">
                    <span className="material-symbols-outlined">video_camera_front</span>
                    <div>
                      <strong>Interview Scheduled</strong>
                      <span>{new Date(application.interview.scheduledDate).toLocaleDateString()}</span>
                      {application.interview.scheduledTime && <span> at {application.interview.scheduledTime}</span>}
                      {application.interview.type && <span> &bull; {application.interview.type}</span>}
                    </div>
                    {application.interview.meetingLink && (
                      <a href={application.interview.meetingLink} target="_blank" rel="noopener noreferrer" className="ma-join-btn">
                        <span className="material-symbols-outlined">open_in_new</span>Join
                      </a>
                    )}
                  </div>
                )}

                <div className="ma-item-actions">
                  <Link to={`/jobs/${job?._id}`} className="ma-btn-view">
                    <span className="material-symbols-outlined">open_in_new</span>View Job
                  </Link>
                </div>

                </div>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyApplications;
